// Globals
import { useState } from 'react';
import { Button, Input } from 'antd';

// Modules
import type { TDebugLogEntry } from '@/modules/Socket';

// Helpers
import { downloadJson, getEntryTags } from '../../helpers';

// Utils
import { formatTime } from '@/utils';

// Components
import { ArrowIcon } from '@/icons';
import {
  StyledDebugPanel,
  StyledHeader,
  StyledLog,
  StyledEntry,
  StyledSender,
  StyledToggleButton,
} from './DebugPanelStyles';

export interface IDebugPanelProps {
  entries: TDebugLogEntry[];
  onClear: () => void;
  onSendRaw: (raw: string) => void;
}

const DIRECTION_ARROW: Record<TDebugLogEntry['direction'], string> = {
  in: '↓',
  out: '↑',
  system: '•',
};

export const DebugPanel = ({ entries, onClear, onSendRaw }: IDebugPanelProps) => {
  const [rawFrame, setRawFrame] = useState('');
  const [collapsed, setCollapsed] = useState(false);

  const handleSend = () => {
    if (!rawFrame.trim()) return;
    onSendRaw(rawFrame);
  };

  const handleDownload = () => {
    downloadJson(`ws-debug-history-${Date.now()}.json`, entries);
  };

  return (
    <StyledDebugPanel $collapsed={collapsed}>
      <StyledHeader>
        <span>WS Debugger ({entries.length})</span>
        <div>
          {!collapsed && (
            <>
              <Button size="small" onClick={handleDownload}>Download history</Button>
              <Button size="small" onClick={onClear}>Clear</Button>
            </>
          )}
          <StyledToggleButton
            size="small"
            $collapsed={collapsed}
            icon={<ArrowIcon />}
            aria-label={collapsed ? 'Expand debugger' : 'Collapse debugger'}
            onClick={() => setCollapsed((prev) => !prev)}
          />
        </div>
      </StyledHeader>

      {!collapsed && (
        <>
          <StyledLog>
            {entries.map((entry) => {
              const tags = getEntryTags(entry);

              return (
                <StyledEntry key={entry.id} $direction={entry.direction}>
                  <summary>
                    <span className="badge">{DIRECTION_ARROW[entry.direction]} {entry.label}</span>
                    <span className="time">{formatTime(new Date(entry.timestamp))}</span>
                  </summary>
                  {tags.length > 0 && (
                    <div className="tags">
                      {tags.map((tag) => <span key={tag} className="tag">{tag}</span>)}
                    </div>
                  )}
                  <pre>{typeof entry.payload === 'string' ? entry.payload : JSON.stringify(entry.payload, null, 2)}</pre>
                </StyledEntry>
              );
            })}
          </StyledLog>

          <StyledSender>
            <Input.TextArea
              rows={3}
              placeholder='{"type": "user_message", "text": "hello"}'
              value={rawFrame}
              onChange={(event) => setRawFrame(event.target.value)}
            />
            <Button size="small" type="primary" onClick={handleSend}>Send raw frame</Button>
          </StyledSender>
        </>
      )}
    </StyledDebugPanel>
  );
};
